'use client';
import { Anchor } from '@repo/ui/src/components/Anchor';
import Markdown from '@repo/ui/src/components/Markdown';
import { classNames } from '@repo/ui/src/utils/classNames';
import { LinkProps } from '@repo/ui/src/types/link';
import Image from '@repo/ui/src/components/Image/New';
import { ImageProps } from '@repo/ui/src/types';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { navigationClicked } from '@repo/ui/src/services/analytics';
import { useVipContext } from './_VipContext';
import React from 'react';

export interface ProductCardProps {
  title?: string;
  price?: string;
  mainLink: LinkProps;
  links?: Array<LinkProps>;
  badge?: string;
  image?: ImageProps;
  primaryHeader?: string;
  secondaryHeader?: string;
  hoverImage?: ImageProps;
}

const useBadgeText = (badge?: string, vip?: boolean) => {
  const { t } = useTranslation();
  if (badge === 'new') {
    return t('productDetailPageContent.newText', undefined, { default: 'New' });
  }
  if (badge === 'sale' && vip === false) {
    return t('productDetailPageContent.saleText', undefined, { default: 'Sale' });
  }
};

const ProductCard = ({
  title,
  price,
  mainLink,
  links,
  badge,
  image,
  hoverImage,
  primaryHeader,
  secondaryHeader,
}: ProductCardProps) => {
  const vip = useVipContext();
  const badgeText = useBadgeText(badge, vip);
  if (!mainLink && !title) return null;
  const hasImage = image?.image?.url;
  const hasHoverImage = hoverImage?.image?.url;

  const mobileImage = hasHoverImage ? hoverImage : image;

  return (
    <div
      className={classNames(
        'text-neutral-700 grid gap-x-4 sm:block',
        hasImage && 'grid-cols-[minmax(0,4fr)_minmax(0,5fr)] items-center',
      )}
      role="group"
      aria-label="product"
    >
      {hasImage && (
        <div className="overflow-hidden mt-2 justify-self-center sm:hidden">
          <Anchor
            {...mainLink}
            className="group inline-block w-full"
            onClick={() =>
              navigationClicked({
                nav_type: 'Top Navigation',
                clicked_text: title ?? '',
                primary_header: primaryHeader ?? '',
                secondary_header: secondaryHeader ?? '',
                tertiary_header: title ?? '',
              })
            }
          >
            <Image
              className="w-full object-cover"
              src={buildImageSrc(mobileImage, 300)}
              width={300}
              height={300}
              alt={mobileImage?.altTag ?? ''}
              data-testid="mobile-image"
            />
          </Anchor>
        </div>
      )}
      <div>
        <Anchor
          {...mainLink}
          className="group inline-block w-full"
          onClick={() =>
            navigationClicked({
              nav_type: 'Top Navigation',
              clicked_text: title ?? '',
              primary_header: primaryHeader ?? '',
              secondary_header: secondaryHeader ?? '',
              tertiary_header: title ?? '',
            })
          }
        >
          <div className="flex flex-row gap-6">
            <h6 className={classNames('tra-text-web-sub-3', mainLink && 'group-hover:underline')}>
              {title}
            </h6>
            {badgeText && (
              <div className="px-2 rounded h-fit bg-brand-400">
                <p className="!text-white tra-text-web-utl-label">{badgeText}</p>
              </div>
            )}
          </div>
          {hasImage && (
            <div className="overflow-hidden mt-2 hidden sm:block group">
              <div className="relative h-[160px] w-full">
                <div
                  className={classNames(
                    'opacity-100 z-10 absolute inset-0 w-full h-full',
                    hasHoverImage && 'group-hover:opacity-0',
                  )}
                >
                  <Image
                    className="h-[155px] w-full object-contain"
                    src={buildImageSrc(image, 300)}
                    width={300}
                    height={300}
                    alt={image?.altTag ?? ''}
                    data-testid="desktop-image"
                  />
                </div>
                {hasHoverImage && (
                  <div className="group-hover:opacity-100 opacity-0 z-20 absolute inset-0 w-full h-full">
                    <Image
                      className="h-[155px] w-full object-contain"
                      src={buildImageSrc(hoverImage, 300)}
                      width={300}
                      height={300}
                      alt={hoverImage?.altTag ?? ''}
                      data-testid="desktop-hover-image"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </Anchor>
        {price && <Markdown className="!mb-0 py-2">{price}</Markdown>}
        <div className="flex flex-row w-full justify-start gap-x-6">
          {links?.map((link, i) => (
            <Anchor
              {...link}
              key={i}
              className="tra-text-web-body-m-link"
              onClick={() =>
                navigationClicked({
                  nav_type: 'Top Navigation',
                  clicked_text: link.displayName ?? '',
                  primary_header: primaryHeader ?? '',
                  secondary_header: secondaryHeader ?? '',
                  tertiary_header: `${title ? title + ' ' : ''}${link.displayName}`,
                })
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
